import { TooltipText } from "@/components/ui/tooltip";
import { Edit2, Check, Trash2, Ban, Printer, Mail, SquarePen } from "lucide-react";

export const getInvoiceColumns = (handlers) => {
    const {
        openEditModal,
        handleIssue,
        handleDelete,
        handleVoid,
        formatDate,
        formatCurrency,
        STATUS_CONFIG,
        TYPE_CONFIG,
        onMail,
        onPrint
    } = handlers;

    return [
        {
            key: "invoice_no",
            label: "INVOICE #",
            render: (invoice) => (
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {invoice.invoice_no}
                </span>
            ),
        },
        {
            key: "invoice_date",
            label: "DATE",
            render: (invoice) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">
                    {formatDate(invoice.invoice_date)}
                </span>
            ),
        },
        {
            key: "due_date",
            label: "DUE DATE",
            render: (invoice) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">
                    {formatDate(invoice.due_date)}
                </span>
            ),
        },
        {
            key: "customer",
            label: "CUSTOMER",
            render: (invoice) => (
                <span className="text-sm text-slate-900 dark:text-white ">
                    {invoice.customer_name || "-"}
                </span>
            ),
        },
        {
            key: "type",
            label: "TYPE",
            render: (invoice) => {
                const typeConfig =
                    TYPE_CONFIG[invoice.invoice_type] || TYPE_CONFIG["INVOICE"];

                return (
                    <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${typeConfig.color} bg-opacity-20 ${typeConfig.color.replace(
                            "bg-",
                            "text-"
                        )}`}
                    >
                        {typeConfig.label}
                    </span>
                );
            },
        },
        {
            key: "amount",
            label: "AMOUNT",
            align: "right",
            render: (invoice) => (
                <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatCurrency(invoice.total_amount)}
                </span>
            ),
        },
        {
            key: "balance",
            label: "BALANCE",
            align: "right",
            render: (invoice) => (
                <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatCurrency(invoice.balance_due)}
                </span>
            ),
        },
        {
            key: "status",
            label: "STATUS",
            render: (invoice) => {
                const statusConfig =
                    STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
                const Icon = statusConfig.icon;

                return (
                    <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color} bg-opacity-20 ${statusConfig.color.replace(
                            "bg-",
                            "text-"
                        )}`}
                    >
                        <Icon className="w-3 h-3" />
                        {statusConfig.label}
                    </span>
                );
            },
        },
        {
            key: "actions",
            label: "ACTIONS",
            render: (invoice) => (
                <div className="flex justify-center gap-1">
                    <TooltipText content={"Edit Invoice"}>
                        <button
                            onClick={() => openEditModal(invoice)}
                            className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                        >
                            <SquarePen className="h-4 w-4" />
                        </button>
                    </TooltipText>
                    <TooltipText content={"Print Invoice"}>
                        <button
                            onClick={() => onPrint(invoice)}
                            className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600"
                        >
                            <Printer className="h-4 w-4" />
                        </button>
                    </TooltipText>
                    <TooltipText content={"Mail Invoice"}>
                        <button
                            onClick={() => onMail(invoice)}
                            className="p-1.5 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600"
                        >
                            <Mail className="h-4 w-4" />
                        </button>
                    </TooltipText>

                    {invoice.status === "draft" && (
                        <>
                            <TooltipText content={"Issue Invoice"}>
                                <button
                                    onClick={() => handleIssue(invoice.id)}
                                    className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600"
                                >
                                    <Check className="w-4 h-4" />
                                </button></TooltipText>
                            <TooltipText content="Delete Invoice">
                                <button
                                    onClick={() => handleDelete(invoice.id)}
                                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </TooltipText>
                        </>
                    )}

                    {["draft", "issued"].includes(invoice.status) &&
                        invoice.paid_amount === 0 && (
                            <TooltipText content="Void Invoice">
                                <button
                                    onClick={() => handleVoid(invoice.id)}
                                    className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600"
                                >
                                    <Ban className="w-4 h-4" />
                                </button>
                            </TooltipText>
                        )}
                </div>
            ),
        },
    ];
};


export const divisionOptions = [
    { label: "Select", value: "" },
    { label: "OCEAN IMPORT", value: "OCEAN IMPORT" },
    { label: "OCEAN EXPORT", value: "OCEAN EXPORT" },
    { label: "AIR IMPORT", value: "AIR IMPORT" },
    { label: "AIR EXPORT", value: "AIR EXPORT" },
    { label: "TRUCKING", value: "TRUCKING" },
    { label: "CUSTOMS BROKERAGE", value: "CUSTOMS BROKERAGE" },
    { label: "WAREHOUSING", value: "WAREHOUSING" },
];

export const typeOptions = [
    { label: "INVOICE", value: "INVOICE" },
    { label: "CREDIT MEMO", value: "CREDIT MEMO" },
    { label: "DEBIT MEMO", value: "DEBIT MEMO" },
];
export const BillTypeOptions = [
    { label: "BILL", value: "BILL" },
    { label: "CREDIT MEMO", value: "CREDIT MEMO" },
    { label: "DEBIT MEMO", value: "DEBIT MEMO" },
];

export const statusOptions = [
    { label: "Draft", value: "draft" },
    { label: "Posted", value: "posted" },
    { label: "Void", value: "void" },
]

export const paymentTermsOptions = [
    { label: "DUE ON RECEIPT", value: "DUE ON RECEIPT" },
    { label: "Net 15", value: "Net 15" },
    { label: "Net 30", value: "Net 30" },
    { label: "Net 45", value: "Net 45" },
    { label: "Net 60", value: "Net 60" },
];

export const officeTypeOptions = [
    { label: "HEAD OFFICE", value: "HEAD OFFICE" },
    { label: "BRANCH", value: "BRANCH" },
    { label: "AGENT", value: "AGENT" },
];

export const currencyOptions = [
    "AED", "AUD", "CAD", "CNY", "EUR", "GBP", "INR", "JPY", "KRW", "MXN", "SGD", "USD"
];

export const PROFIT_CENTERS = ["OCEAN", "AIR", "TRUCKING", "CUSTOMS", "WAREHOUSE", "GENERAL"];
export const basisOptions = [
    "per cntr",
    "per BL",
    "per shipment",
    "per kg",
    "per cbm",
    "lumpsum",
];

export const defaultMetadata = {
    file_reference_no: "",
    customer_po_number: "",
    BookingNumber: "",

    origin_name: "",
    origin_code: "",
    destination_name: "",
    destination_code: "",
    descriptions: "",

    // right column
    division: "OCEAN EXPORT",        // ✅ default set
    type: "INVOICE",                 // ✅ default set
    office_type: "HEAD OFFICE",      // ✅ default set
    on_board_date: "",
    eta_date: "",
    weight_kgs: "",
    measurement_cbm: 0,

    show_wire_transfer: true,        // ✅ usually enabled
    show_zelle_details: false,
    show_credit_card_details: false,

    email: "",
    profit_center: "OCEAN",          // ✅ default set
};